// A multi-repo GOAL as one plan read (plan 101-multi-repo-goal-legs, slice 12): what each leg
// waits on, where each leg's merge is live, and one label for where the whole goal stands.
//
// Every fact here is DERIVED at read, never stored. The stored truth stays where it is:
// the edges in `plan_group_edges` (D2), the verdicts in `deploy_verdicts` (D3), the plan's
// lifecycle in `plans.status`. This module holds the wire shapes the plan projection carries
// per leg (`PlanProjectionGroupSchema.edges` / `.deploy` / `.mergeHeld`) and the pure folds
// over them:
//
//   - {@link planLegDeploys}  — one deploy chip per environment of one leg;
//   - {@link planGoalState}   — the goal's phase, `building` .. `cancelled`;
//   - {@link planLegLive}     — the "N of M legs live" numerator.
//
// "goal" is UI copy only (02-decisions.md § Vocabulary): the identifiers say `plan` and `leg`.
//
// Inputs are STRUCTURAL types, not `PlanProjectionGroup`: `./plan-projection.ts` imports the
// schemas below, so importing it back here would be a cycle.

import { z } from 'zod';
import {
  PlanEdgeHold,
  PlanEdgeHoldSchema,
  PlanEdgeRelease,
  PlanEdgeReleaseSchema,
} from './plan-edge';

/**
 * One edge INTO a leg as the plan read serves it: the stored {@link PlanEdge} fields plus
 * whether it has RELEASED now. `released` is D3's per-edge release, judged in SQL by the one
 * fragment every gate shares (`@developerz/db` `planEdgeReleasedSql`): `merged` = the upstream
 * is satisfied; `deployed` = satisfied and its merge commit's newest verdict in `environment`
 * is a success. Never re-derived in TypeScript.
 */
export const PlanProjectionEdgeSchema = z
  .object({
    upstream: z.string().min(1),
    release: PlanEdgeReleaseSchema,
    environment: z.string().min(1).nullable(),
    hold: PlanEdgeHoldSchema,
    released: z.boolean(),
  })
  .refine((e) => (e.release === PlanEdgeRelease.Deployed) === (e.environment !== null), {
    path: ['environment'],
    message: 'an edge names an environment exactly when release is deployed',
  });
export type PlanProjectionEdge = z.infer<typeof PlanProjectionEdgeSchema>;

/**
 * A `hold: merge` edge into a leg that has NOT released (D4): the leg may build, and its
 * merge is refused `upstream_leg_unreleased` until this edge releases. Projected off
 * {@link PlanProjectionEdgeSchema} by {@link mergeHeldOf}, so it is the same release the
 * merge egress reads (`readUnreleasedMergeEdges`), never a second judgement.
 */
export const PlanLegMergeHoldSchema = z.object({
  upstreamGroupId: z.string().min(1),
  release: PlanEdgeReleaseSchema,
  environment: z.string().min(1).nullable(),
});
export type PlanLegMergeHold = z.infer<typeof PlanLegMergeHoldSchema>;

/** The unreleased `hold: merge` edges of one leg, in edge order. */
export function mergeHeldOf(edges: readonly PlanProjectionEdge[]): PlanLegMergeHold[] {
  return edges
    .filter((e) => e.hold === PlanEdgeHold.Merge && !e.released)
    .map((e) => ({ upstreamGroupId: e.upstream, release: e.release, environment: e.environment }));
}

/**
 * Where one leg's merge stands in one environment.
 *
 *   `deployed` — the merge commit's NEWEST verdict there is a success (the per-environment
 *                newest-wins read `getTaskOutcome` and the edge gate share).
 *   `failed`   — its newest verdict there is a failure (a rollback reads here).
 *   `pending`  — the leg merged, a `deployed` edge out of it names this environment, and no
 *                verdict for the merge commit has arrived there yet.
 *   `unknown`  — the leg merged but the mirrored PR carries no merge commit to join a verdict
 *                on, or a verdict concluded something other than success / failure.
 */
export const PlanLegDeployState = {
  Deployed: 'deployed',
  Failed: 'failed',
  Pending: 'pending',
  Unknown: 'unknown',
} as const;
export const PlanLegDeployStateSchema = z.enum(PlanLegDeployState);
export type PlanLegDeployState = z.infer<typeof PlanLegDeployStateSchema>;

export const PlanLegDeploySchema = z.object({
  environment: z.string().min(1),
  state: PlanLegDeployStateSchema,
  /** The merge commit the verdict is about; null exactly when `state` is `unknown` for want of one. */
  sha: z.string().min(1).nullable(),
});
export type PlanLegDeploy = z.infer<typeof PlanLegDeploySchema>;

/** The facts {@link planLegDeploys} folds for one leg. */
export type PlanLegDeployFacts = {
  /** The leg's PR merged (`plan_groups.status = 'merged'`). */
  readonly merged: boolean;
  /** The mirrored PR's merge commit; null while open or when the mirror never learnt it. */
  readonly sha: string | null;
  /** The standing (newest) verdict per environment for `sha`, as `deploy_verdicts` holds it. */
  readonly verdicts: readonly {
    readonly environment: string;
    readonly conclusion: string | null;
  }[];
  /** The environments some `deployed` edge OUT of this leg waits on. */
  readonly awaitedEnvironments: readonly string[];
};

/**
 * One chip per environment, sorted by name: every environment holding a verdict for the
 * merge commit, plus every environment a dependent waits on once the leg has merged. A leg
 * that has not merged has nothing deployed and nothing to wait for: `[]`.
 */
export function planLegDeploys(facts: PlanLegDeployFacts): PlanLegDeploy[] {
  const out = new Map<string, PlanLegDeploy>();
  for (const v of facts.verdicts) {
    out.set(v.environment, {
      environment: v.environment,
      state: deployStateOf(v.conclusion),
      sha: facts.sha,
    });
  }
  if (facts.merged) {
    for (const environment of facts.awaitedEnvironments) {
      if (out.has(environment)) continue;
      out.set(environment, {
        environment,
        state: facts.sha === null ? PlanLegDeployState.Unknown : PlanLegDeployState.Pending,
        sha: facts.sha,
      });
    }
  }
  return [...out.values()].sort((a, b) => a.environment.localeCompare(b.environment));
}

function deployStateOf(conclusion: string | null): PlanLegDeployState {
  if (conclusion === 'success') return PlanLegDeployState.Deployed;
  if (conclusion === 'failure') return PlanLegDeployState.Failed;
  return PlanLegDeployState.Unknown;
}

/**
 * WHERE THE GOAL STANDS — a read-model label over the plan's legs, NEVER stored: the stored
 * truth is `plans.status` (open / cancelled / done / ended), and a closed plan answers with
 * its own status. For an OPEN plan the first matching rung wins:
 *
 *   `building`  — some work leg is in flight, free to run (no unreleased `hold: start` edge
 *                 into it) and has no open PR yet.
 *   `merging`   — some work leg is in flight with an open PR (a `hold: merge` leg waiting on
 *                 its upstream sits here too).
 *   `deploying` — some `deployed` edge has not released although its upstream leg landed:
 *                 a merge is waiting to go live.
 *   `verifying` — the `live_check` leaf (D6) is in flight with its edges released.
 *   `ended`     — nothing above moves and some leg is stalled or waits behind one: the
 *                 closer (`cron.dead_dependency`) is about to write `plans.status = ended`,
 *                 or a repair ask (D7) will append legs.
 *   `done`      — every leg released its dependents; the closer writes `done` next tick.
 */
export const PlanGoalState = {
  Building: 'building',
  Merging: 'merging',
  Deploying: 'deploying',
  Verifying: 'verifying',
  Done: 'done',
  Ended: 'ended',
  Cancelled: 'cancelled',
} as const;
export const PlanGoalStateSchema = z.enum(PlanGoalState);
export type PlanGoalState = z.infer<typeof PlanGoalStateSchema>;

/**
 * D6's `live_check` outcome kind, spelled here until slice 09 adds it to `OutcomeKind`; that
 * slice replaces this constant with `OutcomeKind.LiveCheck`. Until then no stored task carries
 * it, so no leg is a live-check leaf and no plan reads `verifying`.
 */
export const LIVE_CHECK_OUTCOME_KIND = 'live_check';

/** One leg as {@link planGoalState} reads it. */
export type PlanGoalLeg = {
  readonly groupId: string;
  /**
   * `planGroupDisposition`'s answer for the leg. A `superseded` leg is discharged: it is
   * neither in flight nor stalled, so only its replacement moves the goal's state.
   */
  readonly disposition: 'landed' | 'no_work' | 'in_flight' | 'stalled' | 'superseded';
  /** The leg's PR is mirrored and `open`. */
  readonly pullRequestOpen: boolean;
  /** The leg is the goal's `live_check` leaf (D6). */
  readonly liveCheck: boolean;
  readonly edges: readonly Pick<PlanProjectionEdge, 'upstream' | 'release' | 'hold' | 'released'>[];
};

/** The plan's stored status plus its legs. */
export type PlanGoalFacts = {
  readonly status: 'open' | 'cancelled' | 'done' | 'ended';
  readonly legs: readonly PlanGoalLeg[];
};

/** The goal's phase — see {@link PlanGoalState}. Pure; total over its facts. */
export function planGoalState(facts: PlanGoalFacts): PlanGoalState {
  switch (facts.status) {
    case 'cancelled':
      return PlanGoalState.Cancelled;
    case 'done':
      return PlanGoalState.Done;
    case 'ended':
      return PlanGoalState.Ended;
    case 'open':
      return openGoalState(facts.legs);
  }
}

function openGoalState(legs: readonly PlanGoalLeg[]): PlanGoalState {
  const inFlight = legs.filter((l) => l.disposition === 'in_flight');
  const free = (l: PlanGoalLeg) =>
    !l.edges.some((e) => e.hold === PlanEdgeHold.Start && !e.released);
  const work = inFlight.filter((l) => !l.liveCheck);
  if (work.some((l) => free(l) && !l.pullRequestOpen)) return PlanGoalState.Building;
  if (work.some((l) => l.pullRequestOpen)) return PlanGoalState.Merging;
  if (awaitingDeploy(legs)) return PlanGoalState.Deploying;
  if (inFlight.some((l) => l.liveCheck && free(l))) return PlanGoalState.Verifying;
  if (inFlight.length > 0 || legs.some((l) => l.disposition === 'stalled')) {
    return PlanGoalState.Ended;
  }
  return PlanGoalState.Done;
}

function awaitingDeploy(legs: readonly PlanGoalLeg[]): boolean {
  const landed = new Set(
    legs
      .filter((l) => l.disposition === 'landed' || l.disposition === 'no_work')
      .map((l) => l.groupId),
  );
  // A `superseded` leg is discharged: an edge still INTO it waits on nothing the goal needs.
  return legs.some(
    (l) =>
      l.disposition !== 'superseded' &&
      l.edges.some(
        (e) => e.release === PlanEdgeRelease.Deployed && !e.released && landed.has(e.upstream),
      ),
  );
}

/**
 * Is this leg LIVE — the "N of M legs live" numerator? It delivered (`landed`; a `no_work`
 * leg shipped nothing) and no environment it reports stands anywhere but `deployed`. A leg in
 * a repo with no deploy signal and no `deployed` dependent reports no environment, so its
 * merge is as live as the platform can see.
 */
export function planLegLive(leg: {
  readonly disposition: PlanGoalLeg['disposition'];
  readonly deploy: readonly Pick<PlanLegDeploy, 'state'>[];
}): boolean {
  return (
    leg.disposition === 'landed' && leg.deploy.every((d) => d.state === PlanLegDeployState.Deployed)
  );
}
